import { useEffect } from "react";
import { Box, Collapse, ThemeProvider, Typography, useMediaQuery, useTheme } from "@mui/material";

import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { DialogsProvider } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store.ts";
import { useDispatch, useSelector } from "react-redux";
import { setSignInStep } from "@store/slices/sessionSlice.ts";
import { bpmV2Theme } from "@themes/Bpm.ts";

import { FormularioMFA } from "../components/FormularioMFA.tsx";
import { FormularioInicioSesion } from "../components/FormularioInicioSesion.tsx";
import { FormularioCuentaBloqueada } from "../components/FormularioCuentaBloqueada.tsx";
import { FormularioCambioContrasenia } from "../components/FormularioCambioContrasenia.tsx";
import { FormularioRecuperacionContrasenia } from "../components/FormularioRecuperacionContrasenia.tsx";
import { brandFloatKeyframes, brandPanelGradient, brandShapes, formColumnMaxWidth } from "../components/loginStyles.ts";

import Iso27001Badge from "@assets/iso_27001.png";

export default function Login() {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

    const authState = useSelector((state: RootState) => state.session.auth);

    useEffect(() => {
        fetchAuthSession().then((value) => {
            if (value.tokens === undefined) {
                dispatch(setSignInStep("LOGIN"));
                navigate("/");
            } else if (authState.signInStep === "WELCOME") {
                navigate("/solicitudes");
            }
        });

        if (authState.isAuthenticate === false && authState.isVerified === false) {
            dispatch(setSignInStep("LOGIN"));
        }
    }, []);

    return (
        <DialogsProvider>
            <ThemeProvider theme={bpmV2Theme}>
                <Box sx={{ display: "flex", minHeight: "100vh", width: "100%" }}>
                    <Box
                        sx={{
                            flex: isMobileOrTablet ? "1 1 100%" : "1 1 46%",
                            minWidth: 380,
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "background.paper",
                            px: "clamp(32px, 6vw, 88px)",
                            py: 5,
                        }}
                    >
                        <Box
                            component="img"
                            src={`${import.meta.env.VITE_URL_STATIC_RESOURCES}/lendia/bpm/logo.svg`}
                            alt={"Lendia"}
                            sx={{ height: 34, width: "auto", alignSelf: "flex-start" }}
                        />

                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Box sx={{ width: "100%", maxWidth: formColumnMaxWidth }}>
                                <Collapse in={authState.signInStep === "LOGIN"} unmountOnExit>
                                    <FormularioInicioSesion />
                                </Collapse>
                                <Collapse in={authState.signInStep === "MFA"} unmountOnExit>
                                    <FormularioMFA />
                                </Collapse>
                                <Collapse in={authState.signInStep === "BLOCKED"}>
                                    <FormularioCuentaBloqueada />
                                </Collapse>
                                <Collapse in={authState.signInStep === "RESET_PASS"}>
                                    <FormularioRecuperacionContrasenia />
                                </Collapse>
                                <Collapse in={authState.signInStep === "NEW_PASSWORD"}>
                                    <FormularioCambioContrasenia />
                                </Collapse>
                            </Box>
                        </Box>
                    </Box>

                    {!isMobileOrTablet && (
                        <Box
                            sx={{
                                flex: "1 1 54%",
                                position: "relative",
                                overflow: "hidden",
                                background: brandPanelGradient,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                p: "clamp(40px, 5vw, 72px)",
                                minWidth: 0,
                                ...brandFloatKeyframes,
                            }}
                        >
                            <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
                                {brandShapes.map((shape, index) => (
                                    <Box key={index} sx={shape.sx} />
                                ))}
                            </Box>

                            <Box
                                component="img"
                                src={`${import.meta.env.VITE_URL_STATIC_RESOURCES}/lendia/bpm/logo.svg`}
                                alt={"Lendia"}
                                sx={{
                                    height: 38,
                                    width: "auto",
                                    filter: "brightness(0) invert(1)",
                                    position: "relative",
                                    zIndex: 1,
                                    alignSelf: "flex-start",
                                }}
                            />

                            <Typography
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    maxWidth: 600,
                                    fontFamily: "'Outfit', system-ui, sans-serif",
                                    fontSize: "clamp(30px, 3.6vw, 52px)",
                                    fontWeight: 600,
                                    lineHeight: 1.08,
                                    letterSpacing: "-0.02em",
                                    color: "#fff",
                                }}
                            >
                                Detrás de cada sí comienza una nueva historia.
                            </Typography>

                            <Typography
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    fontSize: "12px",
                                    color: "#A3D4E8",
                                }}
                            >
                                Sesión cifrada de extremo a extremo
                            </Typography>

                            <Box
                                component="img"
                                src={Iso27001Badge}
                                alt="Certificación ISO 27001"
                                sx={{
                                    position: "absolute",
                                    zIndex: 1,
                                    right: "clamp(40px, 5vw, 72px)",
                                    bottom: "clamp(40px, 5vw, 72px)",
                                    height: 56,
                                    width: "auto",
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </ThemeProvider>
        </DialogsProvider>
    );
}
